import { FC } from "react";

import { AttachmentChip } from "./AttachmentChip";
import { BotAvatar } from "./BotAvatar";
import { SuggestionChip } from "./SuggestionChip";
import { AgentChatAttachment, AgentChatMessage, ChatbotClassMap, ChatbotCx } from "../types";

interface Props {
  readonly styles: ChatbotClassMap;
  readonly cx: ChatbotCx;
  readonly messages: readonly AgentChatMessage[];
  readonly onSuggestionSelect?: (suggestion: string) => void;
}

const renderAttachments = (styles: ChatbotClassMap, attachments: readonly AgentChatAttachment[]) => (
  <div className={styles.DiscussionMessageAttachments}>
    {attachments.map((file) => (
      <AttachmentChip key={file.id} styles={styles} file={file} />
    ))}
  </div>
);

/** Scrolling conversation view rendering user and assistant turns with attachments and suggestion chips. */
export const DiscussionView: FC<Props> = ({ styles, cx, messages, onSuggestionSelect }) => (
  <ul className={styles.DiscussionMessages} aria-live="polite" aria-relevant="additions text">
    {messages.map((message) => {
      const isAssistant = message.role === "assistant";
      const attachments = message.attachments ?? [];
      const hasAttached = attachments?.length > 0;
      const suggestions = isAssistant ? (message.suggestions ?? []) : [];
      return (
        <li
          key={message.id}
          className={cx(styles.DiscussionMessageRow, isAssistant ? styles.DiscussionAssistantRow : styles.DiscussionUserRow)}
        >
          {!!isAssistant && <BotAvatar styles={styles} />}
          <div className={cx(styles.DiscussionMessage, isAssistant ? styles.DiscussionAssistantMessage : styles.DiscussionUserMessage)} {...(hasAttached ? { "data-has-attachments": true } : {})}>
            {hasAttached && renderAttachments(styles, attachments)}
            <>{message.content}</>
          </div>
          {suggestions.length > 0 && (
            <div className={styles.DiscussionSuggestions}>
              {suggestions.map((suggestion, index) => (
                <SuggestionChip
                  key={`${suggestion}::${index}`}
                  styles={styles}
                  label={suggestion}
                  onSelect={(value) => onSuggestionSelect?.(value)}
                />
              ))}
            </div>
          )}
        </li>
      );
    })}
  </ul>
);
